import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  fetchConversationListing,
  fetchUserListing,
  setSelectedConversationId,
} from 'src/redux/chatbotConversations'
import ConversationTable from './ConversationTable'
import ConversationThreadPanel from './ConversationThreadPanel'
import UserTable from './UserTable'
import UserConversationList from './UserConversationList'

const TAB_BY_CONVERSATION = 0
const TAB_BY_USER = 1

const MIN_LEFT_WIDTH = 250
const MAX_LEFT_PERCENT = 0.75
const DEFAULT_LEFT_PERCENT = 0.4

const ChatbotConversations = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { selectedUserId } = useSelector(state => state.chatbotConversations)

  const [activeTab, setActiveTab] = useState(TAB_BY_CONVERSATION)
  const [userTabLoaded, setUserTabLoaded] = useState(false)
  const [leftWidthPercent, setLeftWidthPercent] = useState(DEFAULT_LEFT_PERCENT)
  const containerRef = useRef(null)
  const isDragging = useRef(false)

  useEffect(() => {
    dispatch(fetchConversationListing({ page: 1 }))
  }, [dispatch])

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue)
    dispatch(setSelectedConversationId(null))

    if (newValue === TAB_BY_USER && !userTabLoaded) {
      dispatch(fetchUserListing({ page: 1 }))
      setUserTabLoaded(true)
    }
  }

  const handleMouseDown = useCallback(e => {
    e.preventDefault()
    isDragging.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percent = Math.max(
        MIN_LEFT_WIDTH / rect.width,
        Math.min(MAX_LEFT_PERCENT, x / rect.width),
      )
      setLeftWidthPercent(percent)
    }

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [])

  const leftPanelContent =
    activeTab === TAB_BY_CONVERSATION ? (
      <ConversationTable />
    ) : selectedUserId ? (
      <UserConversationList />
    ) : (
      <UserTable />
    )

  const leftWidth = `${leftWidthPercent * 100}%`

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        Chatbot Conversations
      </Typography>

      <Paper
        variant='outlined'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 180px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ borderBottom: `1px solid ${theme.palette.grey[200]}`, flexShrink: 0 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label='By Conversation' />
            <Tab label='By User' />
          </Tabs>
        </Box>

        <Box ref={containerRef} sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              width: leftWidth,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {leftPanelContent}
          </Box>

          <Box
            onMouseDown={handleMouseDown}
            sx={{
              width: 8,
              flexShrink: 0,
              cursor: 'col-resize',
              bgcolor: theme.palette.grey[200],
              '&:hover': { bgcolor: theme.palette.primary.light },
              transition: 'background-color 0.15s',
            }}
          />

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <ConversationThreadPanel />
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default ChatbotConversations
